// ══════════════════════════════════════════════════════════════════════════════════════════════
//  LA VENTANA DEL RELOJ · RELOJ, CUENTA ATRÁS Y CRONÓMETRO                                     (t271, 16-oct-2026)
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  Se abre ENCIMA de la página en la que se está (el botón del reloj de la barra de arriba, utils/relojCabecera.jsx), sin
//  cambiar de página ni tocar nada de lo que hay detrás. La monta la raíz de la app (context/RelojContext.jsx), así que
//  cerrarla no para nada: lo que está en marcha vive en utils/reloj.js y sigue contando.
//
//  Tres pestañas:
//   · «Reloj»: un reloj de agujas grande con su segundero, que se mueve suave (se calcula en cada fotograma con la hora,
//     sin saltos de segundo en segundo), y la hora y la fecha en digital debajo.
//   · «Cuenta atrás»: los tiempos rápidos (30 s, 1, 2, 3, 5, 10 y 15 min) empiezan con un solo toque, que es lo que se
//     agradece con los guantes puestos; o se ponen minutos y segundos a mano y «Empezar». En marcha: lo que queda en
//     grande con un anillo que se va vaciando, «Pausa» / «Seguir», «+1 min» y «Reiniciar». Con la cuenta en marcha los
//     tiempos rápidos no salen: un toque sin querer no puede cambiar un fraguado a medias.
//   · «Cronómetro»: empezar, pausar, vueltas (cada una con su parcial y el total) y reiniciar.
//
//  EL MOVIMIENTO, SOLO SI SE VE: el reloj, el anillo y las centésimas se mueven con requestAnimationFrame mientras esta
//  ventana está abierta y en esa pestaña, y solo con algo en marcha (el reloj de agujas, siempre que se mira). Con la
//  ventana cerrada no hay bucle (se desmonta), y con la pestaña del navegador escondida el navegador no lo llama. Lo que
//  no se para nunca es el cálculo del final, que no está aquí sino en utils/reloj.js.
//
//  Con los tokens de la casa (modo oscuro incluido) y el color de la clínica; las medidas de pantalla, divididas por el
//  tamaño de letra del perfil (`var(--z)`, utils/uiScale).
import { useEffect, useRef, useState } from 'react'
import { Ico } from '../ui'
import {
  DURACIONES_RAPIDAS, formatoTiempo, formatoCrono, restanteDe, transcurridoDe, fraccionRestante, msDeMinutosYSegundos,
  duracionEnPalabras,
} from '../../utils/reloj'
import { prepararSonido } from '../../utils/relojAviso'
import { t as i18nT, tx, marca, tDato, localeFechas } from '../../utils/i18n'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

export const TXT_PESTANAS = { reloj: marca('components.reloj.VentanaReloj.reloj', 'Reloj'), cuenta: marca('components.reloj.VentanaReloj.cuenta_atras', 'Cuenta atrás'), crono: marca('components.reloj.VentanaReloj.cronometro', 'Cronómetro') }
// t271c · el tamaño de la ventana, el mismo en las tres pestañas (ver la caja de abajo).
export const TAMANO_VENTANA = { ancho: '520px', alto: 'min(760px, 100%)' }
const ORDEN = ['reloj', 'cuenta', 'crono']

// La hora, leída en cada pintado; mientras `activo`, se vuelve a pintar en cada fotograma. Lo que se enseña sale siempre
// de la hora del reloj (`ahora()`), nunca de sumar fotogramas.
function useAhora(activo, ahora) {
  const [, setFotograma] = useState(0)
  useEffect(() => {
    if (!activo || typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') return undefined
    let id = 0
    let vivo = true
    const paso = () => { if (!vivo) return; setFotograma((n) => (n + 1) % 1000000); id = window.requestAnimationFrame(paso) }
    id = window.requestAnimationFrame(paso)
    return () => { vivo = false; window.cancelAnimationFrame(id) }
  }, [activo, ahora])
  return ahora()
}

const dos = (n) => String(n).padStart(2, '0')

const BOTON = {
  primario: { background: 'var(--primary)', color: 'var(--sobre-primary)', border: '1px solid var(--primary)' },
  neutro: { background: 'var(--surface)', color: 'var(--text-primary)', border: '1px solid var(--borde-control)' },
}
function Boton({ variante = 'neutro', icono, children, grande = false, style, ...resto }) {
  const apagado = !!resto.disabled
  return (
    <button type="button" {...resto}
      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '7px', minHeight: grande ? '48px' : '42px',
        padding: grande ? '0 22px' : '0 16px', borderRadius: '12px', fontSize: grande ? '16px' : '14px', fontWeight: 800,
        cursor: apagado ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap',
        ...(apagado ? { background: 'var(--surface-2)', color: 'var(--text-muted)', border: '1px solid var(--border)' } : BOTON[variante]), ...style }}>
      {icono && <Ico n={icono} s={grande ? 18 : 16} />}{children}
    </button>
  )
}

// ── «RELOJ» ─────────────────────────────────────────────────────────────────────────────────────
export function RelojDeAgujas({ ahora }) {
  const t = useAhora(true, ahora)
  const d = new Date(t)
  const ms = d.getMilliseconds()
  const s = d.getSeconds() + ms / 1000
  const m = d.getMinutes() + s / 60
  const h = (d.getHours() % 12) + m / 60
  const marcas = []
  for (let i = 0; i < 60; i++) {
    const grande = i % 5 === 0
    marcas.push(<line key={i} x1="100" y1={grande ? 10 : 12} x2="100" y2={grande ? 22 : 17} transform={'rotate(' + (i * 6) + ' 100 100)'}
      stroke={grande ? 'var(--text-primary)' : 'var(--text-muted)'} strokeWidth={grande ? 3 : 1.2} strokeLinecap="round" />)
  }
  const numeros = []
  for (let n = 1; n <= 12; n++) {
    const a = (n * 30 - 90) * Math.PI / 180
    numeros.push(<text key={n} x={100 + 66 * Math.cos(a)} y={100 + 66 * Math.sin(a)} textAnchor="middle" dominantBaseline="central"
      fontSize="14" fontWeight="700" fill="var(--text-secondary)">{n}</text>)
  }
  // «Viernes, 16 de octubre de 2026»: mayúscula solo al principio (con text-transform saldría «De Octubre De»).
  const fecha = (() => {
    try { const f = d.toLocaleDateString(localeFechas(), { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }); return f.charAt(0).toUpperCase() + f.slice(1) } catch (_) { return '' }
  })()
  return (
    <div data-reloj-agujas="" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', padding: '6px 0 4px', margin: 'auto 0' }}>
      <svg viewBox="0 0 200 200" role="img" aria-label={i18nT('components.reloj.VentanaReloj.son_las', 'Son las {{getHours}}:{{dos}}', { getHours: d.getHours(), dos: dos(d.getMinutes()) })}
        style={{ width: '100%', maxWidth: '280px', height: 'auto', display: 'block' }}>
        <circle cx="100" cy="100" r="96" fill="var(--surface-2)" stroke="var(--border)" strokeWidth="2" />
        {marcas}
        {numeros}
        <line data-aguja="hora" x1="100" y1="100" x2="100" y2="52" transform={'rotate(' + (h * 30) + ' 100 100)'}
          stroke="var(--text-primary)" strokeWidth="6" strokeLinecap="round" />
        <line data-aguja="minuto" x1="100" y1="100" x2="100" y2="28" transform={'rotate(' + (m * 6) + ' 100 100)'}
          stroke="var(--text-primary)" strokeWidth="4" strokeLinecap="round" />
        {/* El segundero, en el rojo de aviso de la casa: con cualquier color de clínica (también con el casi negro de
            Biango, que es el de las agujas) se distingue de las otras dos al primer vistazo. */}
        <line data-aguja="segundo" x1="100" y1="116" x2="100" y2="20" transform={'rotate(' + (s * 6) + ' 100 100)'}
          stroke="var(--danger)" strokeWidth="1.8" strokeLinecap="round" />
        <circle cx="100" cy="100" r="5" fill="var(--danger)" />
        <circle cx="100" cy="100" r="2" fill="var(--surface)" />
      </svg>
      <div style={{ fontSize: '36px', fontWeight: 800, fontVariantNumeric: 'tabular-nums', lineHeight: 1, letterSpacing: '-0.01em' }}>
        {dos(d.getHours()) + ':' + dos(d.getMinutes()) + ':' + dos(d.getSeconds())}
      </div>
      {fecha && <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>{fecha}</div>}
    </div>
  )
}

// ── «CUENTA ATRÁS» ──────────────────────────────────────────────────────────────────────────────
const R_ANILLO = 96
const C_ANILLO = 2 * Math.PI * R_ANILLO
function Anillo({ fraccion, color, children }) {
  return (
    <div style={{ position: 'relative', width: '100%', maxWidth: '260px', margin: '0 auto', aspectRatio: '1 / 1' }}>
      <svg viewBox="0 0 220 220" aria-hidden="true" focusable="false" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
        <circle cx="110" cy="110" r={R_ANILLO} fill="none" stroke="var(--border)" strokeWidth="12" />
        <circle data-anillo="" cx="110" cy="110" r={R_ANILLO} fill="none" stroke={color} strokeWidth="12" strokeLinecap="round"
          strokeDasharray={C_ANILLO.toFixed(2)} strokeDashoffset={(C_ANILLO * (1 - Math.max(0, Math.min(1, fraccion)))).toFixed(2)}
          transform="rotate(-90 110 110)" style={{ opacity: fraccion > 0 ? 1 : 0 }} />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
        {children}
      </div>
    </div>
  )
}

export function ATiempoAMano({ reloj, duracion }) {
  const [min, setMin] = useState(() => String(Math.floor(duracion / 60000)))
  const [seg, setSeg] = useState(() => String(Math.floor((duracion % 60000) / 1000)))
  const poner = (m, s) => { setMin(m); setSeg(s); reloj.elegirDuracion(msDeMinutosYSegundos(m, s)) }
  const campo = { width: '84px', padding: '10px 10px', borderRadius: '10px', border: '1px solid var(--borde-control)', background: 'var(--surface)',
    color: 'var(--text-primary)', fontSize: '18px', fontWeight: 700, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }
  const etiqueta = { display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', fontWeight: 700, color: 'var(--text-secondary)' }
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: '10px', flexWrap: 'wrap' }}>
      <label style={etiqueta}>{i18nT('components.reloj.VentanaReloj.minutos', 'Minutos')}
        <input data-reloj-minutos="" type="number" inputMode="numeric" min="0" max="999" value={min} onChange={(e) => poner(e.target.value, seg)} style={campo} />
      </label>
      <label style={etiqueta}>{i18nT('components.reloj.VentanaReloj.segundos', 'Segundos')}
        <input data-reloj-segundos="" type="number" inputMode="numeric" min="0" max="59" value={seg} onChange={(e) => poner(min, e.target.value)} style={campo} />
      </label>
      <Boton variante="primario" icono="play" grande data-reloj-empezar="" disabled={!(duracion > 0)}
        onClick={() => { prepararSonido(); reloj.empezarCuenta() }}>{i18nT('components.reloj.VentanaReloj.empezar', 'Empezar')}</Boton>
    </div>
  )
}

export function CuentaAtras({ reloj, estado }) {
  const c = estado.cuenta
  const t = useAhora(c.estado === 'marcha', reloj.ahora)
  const r = restanteDe(c, t)
  const texto = formatoTiempo(r, 'arriba')
  const cumplida = c.estado === 'cumplida'
  const rotulo = { parada: c.duracion > 0 ? i18nT('components.reloj.VentanaReloj.lista_para_empezar', 'Lista para empezar') : i18nT('components.reloj.VentanaReloj.elige_un_tiempo', 'Elige un tiempo'), marcha: i18nT('components.reloj.VentanaReloj.quedan', 'Quedan'), pausa: i18nT('components.reloj.VentanaReloj.en_pausa', 'En pausa'), cumplida: i18nT('components.reloj.VentanaReloj.tiempo_cumplido', 'Tiempo cumplido') }[c.estado]
  const conToque = (f) => () => { prepararSonido(); f() }
  return (
    <div data-reloj-cuenta-atras={c.estado} style={{ display: 'flex', flexDirection: 'column', gap: '16px', margin: 'auto 0' }}>
      <Anillo fraccion={fraccionRestante(c, t)} color={cumplida ? 'var(--danger)' : 'var(--primary)'}>
        <div style={{ fontSize: '13px', fontWeight: 700, color: cumplida ? 'var(--danger-tinta)' : 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{rotulo}</div>
        <div data-reloj-queda="" style={{ fontSize: texto.length > 5 ? '42px' : '56px', fontWeight: 800, fontVariantNumeric: 'tabular-nums', lineHeight: 1, letterSpacing: '-0.02em' }}>{texto}</div>
        {c.estado !== 'parada' && c.total > 0 && <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{tx('components.reloj.VentanaReloj.de', 'de {{duracionEnPalabras}}', { duracionEnPalabras: duracionEnPalabras(c.total) })}</div>}
      </Anillo>

      {(c.estado === 'parada') && (
        <>
          <div role="group" aria-label={i18nT('components.reloj.VentanaReloj.tiempos_rapidos_empiezan_al_tocarlos', 'Tiempos rápidos: empiezan al tocarlos')} style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '8px' }}>
            {DURACIONES_RAPIDAS.map((d) => (
              <Boton key={d.ms} data-reloj-rapido={String(d.ms)} onClick={conToque(() => reloj.empezarCuenta(d.ms))} style={{ padding: '0 8px', flex: '0 0 auto', minWidth: '84px' }}>{d.texto}</Boton>
            ))}
          </div>
          <div style={{ textAlign: 'center', fontSize: '12px', color: 'var(--text-muted)' }}>{i18nT('components.reloj.VentanaReloj.o_pon_el_tiempo_a', 'o pon el tiempo a mano')}</div>
          <ATiempoAMano reloj={reloj} duracion={c.duracion} />
        </>
      )}
      {c.estado === 'marcha' && (
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <Boton variante="primario" icono="pause" grande data-reloj-pausa="" onClick={() => reloj.pausarCuenta()}>{i18nT('components.reloj.VentanaReloj.pausa', 'Pausa')}</Boton>
          <Boton icono="plus" grande data-reloj-mas="" onClick={conToque(() => reloj.masUnMinuto())}>{i18nT('components.reloj.VentanaReloj.1_min', '+1 min')}</Boton>
          <Boton icono="refresh" grande data-reloj-reiniciar="" onClick={() => reloj.reiniciarCuenta()}>{i18nT('components.reloj.VentanaReloj.reiniciar', 'Reiniciar')}</Boton>
        </div>
      )}
      {c.estado === 'pausa' && (
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <Boton variante="primario" icono="play" grande data-reloj-seguir="" onClick={conToque(() => reloj.seguirCuenta())}>{i18nT('components.reloj.VentanaReloj.seguir', 'Seguir')}</Boton>
          <Boton icono="plus" grande data-reloj-mas="" onClick={conToque(() => reloj.masUnMinuto())}>{i18nT('components.reloj.VentanaReloj.1_min', '+1 min')}</Boton>
          <Boton icono="refresh" grande data-reloj-reiniciar="" onClick={() => reloj.reiniciarCuenta()}>{i18nT('components.reloj.VentanaReloj.reiniciar', 'Reiniciar')}</Boton>
        </div>
      )}
      {cumplida && (
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <Boton variante="primario" grande data-reloj-parar-ventana="" onClick={() => reloj.pararAviso()}>{i18nT('components.reloj.VentanaReloj.parar', 'Parar')}</Boton>
          <Boton icono="plus" grande data-reloj-mas="" onClick={conToque(() => reloj.masUnMinuto())}>{i18nT('components.reloj.VentanaReloj.1_min', '+1 min')}</Boton>
        </div>
      )}

      <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontSize: '14px', color: 'var(--text-primary)', cursor: 'pointer' }}>
        <input type="checkbox" data-reloj-sonido="" checked={!!estado.sonido} onChange={(e) => reloj.ponerSonido(e.target.checked)}
          style={{ width: '18px', height: '18px', accentColor: 'var(--primary)' }} />
        {i18nT('components.reloj.VentanaReloj.sonido_al_terminar', 'Sonido al terminar')}
      </label>
      <div style={{ textAlign: 'center', fontSize: '12.5px', color: 'var(--text-muted)', lineHeight: 1.45 }}>
        {i18nT('components.reloj.VentanaReloj.sigue_contando_aunque_cierres_esta', 'Sigue contando aunque cierres esta ventana o cambies de página: el reloj de la barra de arriba enseña lo que queda y, al terminar, la pantalla parpadea.')}
      </div>
    </div>
  )
}

// ── «CRONÓMETRO» ────────────────────────────────────────────────────────────────────────────────
export function Cronometro({ reloj, estado }) {
  const k = estado.crono
  const t = useAhora(k.estado === 'marcha', reloj.ahora)
  const v = transcurridoDe(k, t)
  const vueltas = k.vueltas.map((x, i) => ({ n: i + 1, ...x })).reverse()
  return (
    // t271c · las vueltas se deslizan DENTRO de su lista: el reloj y los botones se quedan quietos y la ventana no crece.
    <div data-reloj-crono={k.estado} style={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'stretch', flex: '1 1 auto', minHeight: 0,
      justifyContent: vueltas.length ? 'flex-start' : 'center' }}>
      <div style={{ textAlign: 'center', padding: '22px 0 6px', flexShrink: 0 }}>
        <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {{ parada: i18nT('components.reloj.VentanaReloj.listo_para_empezar', 'Listo para empezar'), marcha: i18nT('components.reloj.VentanaReloj.en_marcha', 'En marcha'), pausa: i18nT('components.reloj.VentanaReloj.en_pausa', 'En pausa') }[k.estado]}
        </div>
        <div data-reloj-lleva="" style={{ marginTop: '6px', fontSize: '52px', fontWeight: 800, fontVariantNumeric: 'tabular-nums', lineHeight: 1, letterSpacing: '-0.02em' }}>
          {formatoCrono(v)}
        </div>
      </div>
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap', flexShrink: 0 }}>
        {k.estado === 'parada' && <Boton variante="primario" icono="play" grande data-crono-empezar="" onClick={() => reloj.empezarCrono()}>{i18nT('components.reloj.VentanaReloj.empezar', 'Empezar')}</Boton>}
        {k.estado === 'marcha' && <Boton variante="primario" icono="pause" grande data-crono-pausa="" onClick={() => reloj.pausarCrono()}>{i18nT('components.reloj.VentanaReloj.pausa', 'Pausa')}</Boton>}
        {k.estado === 'marcha' && <Boton icono="plus" grande data-crono-vuelta="" onClick={() => reloj.vueltaCrono()}>{i18nT('components.reloj.VentanaReloj.vuelta', 'Vuelta')}</Boton>}
        {k.estado === 'pausa' && <Boton variante="primario" icono="play" grande data-crono-seguir="" onClick={() => reloj.seguirCrono()}>{i18nT('components.reloj.VentanaReloj.seguir', 'Seguir')}</Boton>}
        {k.estado === 'pausa' && <Boton icono="refresh" grande data-crono-reiniciar="" onClick={() => reloj.reiniciarCrono()}>{i18nT('components.reloj.VentanaReloj.reiniciar', 'Reiniciar')}</Boton>}
      </div>
      {vueltas.length > 0 && (
        <ol data-crono-vueltas="" aria-label={i18nT('components.reloj.VentanaReloj.vueltas', 'Vueltas')} style={{ listStyle: 'none', margin: 0, padding: 0, border: '1px solid var(--border)', borderRadius: '12px',
          flex: '0 1 auto', minHeight: '84px', overflowY: 'auto', overscrollBehavior: 'contain' }}>
          {vueltas.map((x, i) => (
            <li key={x.n} style={{ display: 'flex', alignItems: 'baseline', gap: '10px', padding: '9px 14px', borderTop: i ? '1px solid var(--border-light)' : 'none',
              fontVariantNumeric: 'tabular-nums', fontSize: '14px' }}>
              <span style={{ fontWeight: 700, color: 'var(--text-secondary)', minWidth: '72px' }}>{tx('components.reloj.VentanaReloj.vuelta_2', 'Vuelta {{n}}', { n: x.n })}</span>
              <span style={{ fontWeight: 800, marginLeft: 'auto' }}>{formatoCrono(x.parcial)}</span>
              <span style={{ color: 'var(--text-muted)', minWidth: '96px', textAlign: 'right' }}>{tx('components.reloj.VentanaReloj.total', 'total {{formatoCrono}}', { formatoCrono: formatoCrono(x.total) })}</span>
            </li>
          ))}
        </ol>
      )}
      <div style={{ textAlign: 'center', fontSize: '12.5px', color: 'var(--text-muted)', flexShrink: 0 }}>{i18nT('components.reloj.VentanaReloj.sigue_contando_aunque_cierres_esta_2', 'Sigue contando aunque cierres esta ventana.')}</div>
    </div>
  )
}

// ── LA VENTANA ──────────────────────────────────────────────────────────────────────────────────
export default function VentanaReloj({ reloj, estado }) {
  const caja = useRef(null)
  const pestana = ORDEN.includes(estado.pestana) ? estado.pestana : 'reloj'
  const cerrar = () => reloj.cerrar()

  useEffect(() => { try { if (caja.current && caja.current.focus) caja.current.focus() } catch (_) { /* sin foco */ } }, [])
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') return undefined
    const tecla = (e) => { if (e && e.key === 'Escape') reloj.cerrar() }
    window.addEventListener('keydown', tecla)
    return () => window.removeEventListener('keydown', tecla)
  }, [reloj])

  return (
    <div className="modal-safe" data-ventana-reloj="" onMouseDown={(e) => { if (e.target === e.currentTarget) cerrar() }}
      style={{ position: 'fixed', inset: 0, zIndex: 9700, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div ref={caja} role="dialog" aria-modal="true" aria-labelledby="reloj-ventana-titulo" tabIndex={-1}
        style={{ background: 'var(--surface)', color: 'var(--text-primary)', borderRadius: '18px', border: '1px solid var(--border)', boxShadow: 'var(--shadow-lg)',
          // t271c · EL MISMO TAMAÑO EN LAS TRES PESTAÑAS (el dueño: «que aunque cambies de pestaña entre reloj, cuenta atrás y
          // cronómetro, el tamaño de la caja sea igual y no vaya cambiando»): ancho y alto fijos, los de la pestaña más
          // grande (la cuenta atrás con los tiempos rápidos), o lo que deje la pantalla; el contenido, centrado dentro.
          // `100%` es del velo, que ya va dividido por el tamaño de letra del perfil (.modal-safe, var(--z)).
          width: '100%', maxWidth: TAMANO_VENTANA.ancho, height: TAMANO_VENTANA.alto, maxHeight: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', outline: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 14px 10px 20px', flexShrink: 0 }}>
          <h2 id="reloj-ventana-titulo" style={{ margin: 0, fontSize: '18px', fontWeight: 800 }}>{i18nT('components.reloj.VentanaReloj.reloj', 'Reloj')}</h2>
          <button type="button" onClick={cerrar} aria-label={i18nT('components.reloj.VentanaReloj.cerrar', 'Cerrar')} title={i18nT('components.reloj.VentanaReloj.cerrar', 'Cerrar')}
            style={{ marginLeft: 'auto', width: '38px', height: '38px', borderRadius: '10px', border: '1px solid var(--border)', background: 'var(--surface-2)',
              color: 'var(--text-secondary)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <Ico n="close" s={18} />
          </button>
        </div>
        <div role="tablist" aria-label={i18nT('components.reloj.VentanaReloj.reloj_cuenta_atras_y_cronometro', 'Reloj, cuenta atrás y cronómetro')}
          style={{ display: 'flex', gap: '6px', padding: '0 14px 12px', flexShrink: 0, borderBottom: '1px solid var(--border)' }}>
          {ORDEN.map((p) => {
            const activa = p === pestana
            return (
              <button key={p} type="button" role="tab" aria-selected={activa} data-reloj-pestana={p} onClick={() => reloj.elegirPestana(p)}
                style={{ flex: '1 1 0', minWidth: 0, minHeight: '40px', padding: '0 8px', borderRadius: '10px', fontSize: '14px', fontWeight: 800, cursor: 'pointer',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  ...(activa
                    ? { background: 'var(--primary)', color: 'var(--sobre-primary)', border: '1px solid var(--primary)' }
                    : { background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border)' }) }}>
                {tDato(TXT_PESTANAS[p])}
              </button>
            )
          })}
        </div>
        <div role="tabpanel" aria-label={tDato(TXT_PESTANAS[pestana])}
          style={{ padding: '16px 20px 20px', overflowY: 'auto', overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch', minHeight: 0,
            flex: '1 1 auto', display: 'flex', flexDirection: 'column' }}>
          {pestana === 'reloj' && <RelojDeAgujas ahora={reloj.ahora} />}
          {pestana === 'cuenta' && <CuentaAtras reloj={reloj} estado={estado} />}
          {pestana === 'crono' && <Cronometro reloj={reloj} estado={estado} />}
        </div>
      </div>
    </div>
  )
}
